/**
 * product-size.js -- keyboard navigation for the PDP size chips.
 *
 * The size selector (`snippets/product-size-selector.liquid`) renders a
 * <fieldset> of radio chips. Every chip - available OR sold out - behaves
 * as a normal radio: selecting one fires `change`, which `product-form.js`
 * routes through `toggleAvailability`. For an available size this keeps
 * Add-to-Cart visible; for a sold-out size it hides Add-to-Cart and reveals
 * the inline back-in-stock (Notify Me) form, matching the live storefront.
 *
 * Sold-out chips are therefore intentionally selectable (no `disabled`, no
 * `aria-disabled`, normally tabbable). Their `data-available="false"` flag
 * and struck-through styling convey the out-of-stock state; the radio's
 * `aria-label` announces "sold out" to screen readers.
 *
 * This script only adds roving arrow-key navigation across every chip so
 * keyboard users can move through the group and select any size (including
 * sold-out ones) with the arrow keys.
 *
 * No framework. Vanilla JS, defer-loaded once per PDP.
 */

(function () {
  'use strict';

  function inputs(fieldset) {
    return Array.from(
      fieldset.querySelectorAll('.product-size-selector__input'),
    );
  }

  function selectChip(input) {
    // Native radio select - sold-out and available chips behave the same;
    // `product-form.js` decides ATC vs back-in-stock from `data-available`.
    input.focus();
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function onKeydown(fieldset) {
    return function handler(event) {
      const key = event.key;
      const forward = key === 'ArrowRight' || key === 'ArrowDown';
      const backward = key === 'ArrowLeft' || key === 'ArrowUp';
      if (!forward && !backward) return;

      const all = inputs(fieldset);
      if (!all.length) return;
      const currentIndex = all.indexOf(document.activeElement);
      if (currentIndex === -1) return;

      event.preventDefault();
      const delta = forward ? 1 : -1;
      const next = all[(currentIndex + delta + all.length) % all.length];

      selectChip(next);
    };
  }

  function mount(fieldset) {
    if (fieldset.hasAttribute('data-product-size-mounted')) return;
    fieldset.setAttribute('data-product-size-mounted', '');
    fieldset.addEventListener('keydown', onKeydown(fieldset));
  }

  function init() {
    document
      .querySelectorAll('[data-product-size-selector]')
      .forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
